import { ITableData } from 'types/excelExport';
import {
    IComparisonReportData, IComparisonReportUses, IInvestmentMetricsData, IProjectionMetricsData,
} from 'views/Reports/types';
import { bpsFormatter, formatDecimalAsPercentage, formatUSDCurrencyTwoDigits } from 'ns_libs/formatter';
import {
    COMPARISON_REPORT_GROUPS, COMPARISON_REPORT_GROUPS_MAP, ComparisonReportPropertyGroup, ComparisonReportRow,
} from './constants';

const EXCEL_NSBLUE = 'e6e8e8';
const EXCEL_WHITE = '0FB3FF';
const BORDER_COLOR = '000000';
const EXCEL_DARK_GRAY = 'BFBFBF';
const EXCEL_LIGHT_GREEN = '0042D29D';

const defaultFontSize = 12;
const borderStyle = { style: 'medium', color: { argb: BORDER_COLOR } };
const thinBorderStyle = { style: 'thin', color: { argb: BORDER_COLOR } };
const dashedBorderStyle = { style: 'dashed', color: { argb: BORDER_COLOR } };

export const formatExcelExportData = (title: string, subtitle: string, data: IComparisonReportData[]) => {
    const conjunctionGroups = [
        ComparisonReportPropertyGroup.PROPERTY_RETURNS,
        ComparisonReportPropertyGroup.PARTNERSHIP_RETURNS,
        ComparisonReportPropertyGroup.DEVELOPMENT_RETURNS,
        ComparisonReportPropertyGroup.USES,
        ComparisonReportPropertyGroup.INCOME,
    ];

    const topHeaderRows = [
        {
            rowFont: { bold: true, size: defaultFontSize },
            rowCells: [{ value: title }],
        },
        {
            rowFont: { bold: true, size: defaultFontSize },
            rowCells: [{ value: subtitle }],
        },
    ];

    const tableHeaderLabelsRow = {
        rowFont: {
            bold: true,
            size: 12,
            color: { argb: EXCEL_NSBLUE },
        },
        rowFill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: EXCEL_WHITE },
        },
        rowCells: [
            {
                value: '',
                width: 25,
                border: {
                    top: borderStyle,
                    left: borderStyle,
                    bottom: thinBorderStyle,
                    right: thinBorderStyle,
                },
            },
            ...data.map((col, idx) => ({
                value: `${col.dealName} - ${col.proFormaName}`,
                width: 42,
                border: {
                    top: borderStyle,
                    right: idx === data.length - 1 ? borderStyle : dashedBorderStyle,
                    bottom: thinBorderStyle,
                },
                alignment: { horizontal: 'right' },
            })),
        ],
    };

    const tableData = COMPARISON_REPORT_GROUPS.flatMap((group, groupIdx) => {
        const groupRow: ITableData = {
            rowFill: {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: EXCEL_DARK_GRAY },
            },
            rowCells: [
                {
                    value: group,
                    border: {
                        top: thinBorderStyle,
                        left: borderStyle,
                        right: thinBorderStyle,
                    },
                    width: 25,
                },
                ...data.map((col, idx) => ({
                    value: '',
                    border: {
                        top: thinBorderStyle,
                        right: idx === data.length - 1 ? borderStyle : dashedBorderStyle,
                    },
                })),
            ],
        };

        const additionalRows = [];
        if (conjunctionGroups.includes(group)) {
            additionalRows.push(
                ...COMPARISON_REPORT_GROUPS_MAP[group].flatMap((row, rowId) => {
                    let lastRowBottomBorder = dashedBorderStyle;
                    if (rowId === COMPARISON_REPORT_GROUPS_MAP[group].length - 1) {
                        lastRowBottomBorder = thinBorderStyle;
                        if (groupIdx === COMPARISON_REPORT_GROUPS.length - 1) {
                            lastRowBottomBorder = borderStyle;
                        }
                    }
                    const firstRow: ITableData = {
                        rowCells: [
                            {
                                value: '',
                                border: {
                                    left: borderStyle,
                                    right: thinBorderStyle,
                                },
                                width: 25,
                            },
                            ...data.map((col, idx) => {
                                const [value, isHighlighted] = getComparisonReportValueByProperty(col, group, row);
                                return {
                                    value: Array.isArray(value) ? (value as (string | number)[])[0] : '',
                                    border: {
                                        right: idx === data.length - 1 ? borderStyle : dashedBorderStyle,
                                    },
                                    alignment: { horizontal: 'right' },
                                    ...(isHighlighted && {
                                        cellFill: {
                                            type: 'pattern',
                                            pattern: 'solid',
                                            fgColor: { argb: EXCEL_LIGHT_GREEN },
                                        },
                                    }),
                                };
                            }),
                        ],
                    };
                    const secondRow: ITableData = {
                        rowCells: [
                            {
                                value: row,
                                border: {
                                    left: borderStyle,
                                    right: thinBorderStyle,
                                },
                                width: 25,
                            },
                            ...data.map((col, idx) => {
                                const [value, isHighlighted] = getComparisonReportValueByProperty(col, group, row);
                                return {
                                    value: Array.isArray(value) ? (value as (string | number)[])[1] : '',
                                    border: {
                                        right: idx === data.length - 1 ? borderStyle : dashedBorderStyle,
                                    },
                                    alignment: { horizontal: 'right' },
                                    ...(isHighlighted && {
                                        cellFill: {
                                            type: 'pattern',
                                            pattern: 'solid',
                                            fgColor: { argb: EXCEL_LIGHT_GREEN },
                                        },
                                    }),
                                };
                            }),
                        ],
                    };
                    const thirdRow: ITableData = {
                        rowCells: [
                            {
                                value: '',
                                border: {
                                    left: borderStyle,
                                    right: thinBorderStyle,
                                    bottom: lastRowBottomBorder,
                                },
                                width: 25,
                            },
                            ...data.map((col, idx) => {
                                const [value, isHighlighted] = getComparisonReportValueByProperty(col, group, row);
                                return {
                                    value: Array.isArray(value) ? (value as (string | number)[])[2] : '',
                                    border: {
                                        right: idx === data.length - 1 ? borderStyle : dashedBorderStyle,
                                        bottom: lastRowBottomBorder,
                                    },
                                    alignment: { horizontal: 'right' },
                                    ...(isHighlighted && {
                                        cellFill: {
                                            type: 'pattern',
                                            pattern: 'solid',
                                            fgColor: { argb: EXCEL_LIGHT_GREEN },
                                        },
                                    }),
                                };
                            }),
                        ],
                    };
                    return [firstRow, secondRow, thirdRow];
                }),
            );
        } else {
            additionalRows.push(
                ...COMPARISON_REPORT_GROUPS_MAP[group].map((row, rowIdx) => {
                    const newRow: ITableData = {
                        rowCells: [
                            {
                                value: row,
                                border: {
                                    left: borderStyle,
                                    right: thinBorderStyle,
                                    bottom:
                                        rowIdx === COMPARISON_REPORT_GROUPS_MAP[group].length - 1 && groupIdx === COMPARISON_REPORT_GROUPS.length - 1
                                            ? borderStyle
                                            : undefined,
                                },
                                width: 25,
                            },
                            ...data.map((col, idx) => ({
                                value: getComparisonReportSingleValueByProperty(col, group, row),
                                border: {
                                    right: idx === data.length - 1 ? borderStyle : dashedBorderStyle,
                                    bottom:
                                        rowIdx === COMPARISON_REPORT_GROUPS_MAP[group].length - 1 && groupIdx === COMPARISON_REPORT_GROUPS.length - 1
                                            ? borderStyle
                                            : undefined,
                                },
                                alignment: { horizontal: 'right' },
                            })),
                        ],
                    };
                    return newRow;
                }),
            );
        }

        return [groupRow, ...additionalRows];
    });

    return {
        topHeaderRows,
        reportTables: [
            {
                tableHeaderRows: [tableHeaderLabelsRow],
                tableData,
                tableFooter: {
                    rowCells: [],
                },
            },
        ],
    };
};

const getComparisonReportValueByProperty = (data: IComparisonReportData, propertyGroup: ComparisonReportPropertyGroup, property: ComparisonReportRow) => {
    const formatReturns = (returns: IInvestmentMetricsData) => [
        `ROI: ${returns.roi ? formatDecimalAsPercentage(returns.roi, 2) : 'n/a'}`,
        `EMx: ${returns.emx ? `${returns.emx}x` : 'n/a'}`,
        `IRR: ${returns.irr ? formatDecimalAsPercentage(returns.irr, 2) : 'n/a'}`,
    ];

    const formatPercentMetrics = (metrics: IProjectionMetricsData) => [
        `Untrended: ${metrics.untrended ? formatDecimalAsPercentage(metrics.untrended, 2) : 'n/a'}`,
        `Trended: ${metrics.trended ? formatDecimalAsPercentage(metrics.trended, 2) : 'n/a'}`,
        `Sale: ${metrics.sale ? formatDecimalAsPercentage(metrics.sale, 2) : 'n/a'}`,
    ];

    const formatCurrencyMetrics = (metrics: IProjectionMetricsData) => [
        `Untrended: ${metrics.untrended ? formatUSDCurrencyTwoDigits(metrics.untrended) : 'n/a'}`,
        `Trended: ${metrics.trended ? formatUSDCurrencyTwoDigits(metrics.trended) : 'n/a'}`,
        `Sale: ${metrics.sale ? formatUSDCurrencyTwoDigits(metrics.sale) : 'n/a'}`,
    ];

    const formatBPSMetrics = (metrics: IProjectionMetricsData) => [
        `Untrended: ${metrics.untrended ? bpsFormatter(metrics.untrended) : 'n/a'}`,
        `Trended: ${metrics.trended ? bpsFormatter(metrics.trended) : 'n/a'}`,
        `Sale: ${metrics.sale ? bpsFormatter(metrics.sale) : 'n/a'}`,
    ];

    if (propertyGroup === ComparisonReportPropertyGroup.PROPERTY_RETURNS) {
        const { propertyReturns } = data;

        if (property === ComparisonReportRow.UNLEVERED) {
            return [formatReturns(propertyReturns.unlevered), propertyReturns.unlevered.isHighlighted || false];
        }
        if (property === ComparisonReportRow.LEVERED) {
            return [formatReturns(propertyReturns.levered), propertyReturns.levered.isHighlighted || false];
        }
    } else if (propertyGroup === ComparisonReportPropertyGroup.PARTNERSHIP_RETURNS) {
        const { partnershipReturns } = data;

        if (property === ComparisonReportRow.LIMITED_PARTNER) {
            return [formatReturns(partnershipReturns.limitedPartner), partnershipReturns.limitedPartner.isHighlighted || false];
        }
        if (property === ComparisonReportRow.GENERAL_PARTNER) {
            return [formatReturns(partnershipReturns.generalPartner), partnershipReturns.generalPartner.isHighlighted || false];
        }
    } else if (propertyGroup === ComparisonReportPropertyGroup.DEVELOPMENT_RETURNS) {
        const { developmentReturns } = data;

        if (property === ComparisonReportRow.YIELD_ON_COST) {
            return [formatPercentMetrics(developmentReturns.yieldOnCost), developmentReturns.yieldOnCost.isHighlighted || false];
        }
        if (property === ComparisonReportRow.MARKET_CAP_RATE) {
            return [formatPercentMetrics(developmentReturns.marketCapRate), false];
        }
        if (property === ComparisonReportRow.DEVELOPMENT_SPREAD) {
            return [formatBPSMetrics(developmentReturns.developmentSpread), false];
        }
    } else if (propertyGroup === ComparisonReportPropertyGroup.USES) {
        const {
            acquisitionCosts, hardCosts, softCosts, carryCosts, totalUses,
        } = data.uses;

        const formatCosts = (costs: IComparisonReportUses) => [
            `Amount: ${formatUSDCurrencyTwoDigits(costs.amount)}`,
            `$ / Build SF: ${formatUSDCurrencyTwoDigits(costs.costPerBuiltSf)}`,
            `$ / Unit: ${formatUSDCurrencyTwoDigits(costs.costPerUnit)}`];

        if (property === ComparisonReportRow.ACQUISITION_COSTS) {
            return [formatCosts(acquisitionCosts), false];
        }
        if (property === ComparisonReportRow.HARD_COSTS) {
            return [formatCosts(hardCosts), false];
        }
        if (property === ComparisonReportRow.SOFT_COSTS) {
            return [formatCosts(softCosts), false];
        }
        if (property === ComparisonReportRow.CARRY_COSTS) {
            return [formatCosts(carryCosts), false];
        }
        if (property === ComparisonReportRow.TOTAL_USES) {
            return [formatCosts(totalUses), totalUses.isHighlighted || false];
        }
    } else if (propertyGroup === ComparisonReportPropertyGroup.INCOME) {
        const {
            effectiveGrossRevenue,
            operatingExpenses,
            netOperatingIncome,
            capitalExpenditures,
            cashFlowFromOperations,
            capRate,
            valuation,
            percentOfTotalProjectCost,
            valuationPerUnit,
        } = data.income;

        if (property === ComparisonReportRow.EFFECTIVE_GROSS_REVENUE) {
            return [formatCurrencyMetrics(effectiveGrossRevenue), false];
        }
        if (property === ComparisonReportRow.OPERATING_EXPENSES) {
            return [formatCurrencyMetrics(operatingExpenses), false];
        }
        if (property === ComparisonReportRow.NET_OPERATING_INCOME) {
            return [formatCurrencyMetrics(netOperatingIncome), false];
        }
        if (property === ComparisonReportRow.CAPITAL_EXPENDITURES) {
            return [formatCurrencyMetrics(capitalExpenditures), false];
        }
        if (property === ComparisonReportRow.CASH_FLOW_FROM_OPERATIONS) {
            return [formatCurrencyMetrics(cashFlowFromOperations), false];
        }
        if (property === ComparisonReportRow.CAP_RATE) {
            return [formatPercentMetrics(capRate), false];
        }
        if (property === ComparisonReportRow.VALUATION) {
            return [formatCurrencyMetrics(valuation), false];
        }
        if (property === ComparisonReportRow.PERCENT_OF_TOTAL_PROJECT_COST) {
            return [formatPercentMetrics(percentOfTotalProjectCost), false];
        }
        if (property === ComparisonReportRow.VALUATION_PER_UNIT) {
            return [formatCurrencyMetrics(valuationPerUnit), false];
        }
    }
    return [[0, 0, 0], false];
};

const getComparisonReportSingleValueByProperty = (
    data: IComparisonReportData,
    propertyGroup: ComparisonReportPropertyGroup,
    property: ComparisonReportRow,
) => {
    if (propertyGroup === ComparisonReportPropertyGroup.DETAILS) {
        const {
            units, buildings, height, landArea, netRentableSf, parkingRatio, parkingSpaces,
        } = data.details;
        if (property === ComparisonReportRow.UNITS) {
            return units || 'n/a';
        }
        if (property === ComparisonReportRow.BUILDINGS) {
            return buildings || 'n/a';
        }
        if (property === ComparisonReportRow.HEIGHT) {
            return height || 'n/a';
        }
        if (property === ComparisonReportRow.LAND_AREA) {
            return landArea || 'n/a';
        }
        if (property === ComparisonReportRow.NET_RENTABLE_SF) {
            return netRentableSf || 'n/a';
        }
        if (property === ComparisonReportRow.PARKING_RATIO) {
            return parkingRatio || 'n/a';
        }
        if (property === ComparisonReportRow.PARKING_SPACES) {
            return parkingSpaces || 'n/a';
        }
    }
    return 0;
};
